import React, { useEffect, useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { SEO, pageSEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CreditCard, Wallet, Building2, Heart, Briefcase, Users, TrendingUp, Loader2 } from 'lucide-react';
import { stripeService } from '../services/stripeService';
import { paypalService } from '../services/paypalService';
export function DonatePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    amount: '',
    currency: 'EUR'
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  const paypalContainerRef = useRef<HTMLDivElement>(null);
  // Load PayPal hosted button when PayPal is selected
  useEffect(() => {
    if (paymentMethod === 'paypal' && !paypalReady) {
      setPaypalLoading(true);
      paypalService.renderHostedButton('paypal-button-container').then(success => {
        setPaypalLoading(false);
        setPaypalReady(success);
        if (!success) {
          console.log('PayPal hosted button not available, will use redirect fallback');
        }
      });
    }
  }, [paymentMethod]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Manual validation with clear messages
    if (!formData.firstName.trim()) {
      alert('Veuillez entrer votre prénom.');
      return;
    }
    if (!formData.lastName.trim()) {
      alert('Veuillez entrer votre nom.');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      alert('Veuillez entrer une adresse email valide.');
      return;
    }
    if (!formData.amount) {
      alert('Veuillez entrer un montant.');
      return;
    }
    if (!paymentMethod) {
      alert('Veuillez sélectionner un moyen de paiement.');
      return;
    }
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Veuillez entrer un montant valide.');
      return;
    }
    setIsProcessing(true);
    const donationData = {
      amount,
      currency: formData.currency.toLowerCase(),
      donorName: `${formData.firstName} ${formData.lastName}`,
      donorEmail: formData.email,
      companyName: formData.companyName
    };
    try {
      let result;
      switch (paymentMethod) {
        case 'stripe':
          // Stripe redirects the page, so no need to handle result
          result = await stripeService.createCheckoutSession(donationData);
          if (!result.success) {
            alert(result.error || 'Une erreur est survenue avec Stripe.');
          }
          break;
        case 'paypal':
          // If PayPal hosted button is rendered, user clicks it directly
          if (paypalReady) {
            alert('Veuillez utiliser le bouton PayPal affiché ci-dessus.');
            setIsProcessing(false);
            return;
          }
          // Fallback: redirect to PayPal
          result = await paypalService.createPayment(donationData);
          if (!result.success) {
            alert(result.error || 'Une erreur est survenue avec PayPal.');
          }
          break;
        default:
          alert('Méthode de paiement non supportée.');
          break;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Une erreur est survenue lors du traitement du paiement.');
    } finally {
      setIsProcessing(false);
    }
  };
  return <Layout data-id="element-1092">
      <SEO {...pageSEO.donate} data-id="element-1093" />
      <div className="bg-[#2C3E50] text-white py-16" data-id="element-1094">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1095">
          <h1 className="text-4xl font-bold mb-4" data-id="element-1096">Faire un don</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-6" data-id="element-1097">
            Votre soutien permet non seulement de développer les compétences,
            mais aussi de financer des dispositifs d'accompagnement à
            l'insertion des bénéficiaires vers un emploi durable, et de
            structurer ainsi un modèle d'aide à l'emploi éthique et responsable.
          </p>
          <div className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3" data-id="element-1098">
            <p className="text-lg font-semibold" data-id="element-1099">
              Soutenez l'insertion professionnelle durable.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1101">
          <Card className="bg-blue-50 border-blue-100" data-id="element-1102">
            <CardContent className="p-6 text-center" data-id="element-1103">
              <Users className="w-8 h-8 text-[#1E88E5] mx-auto mb-4" data-id="element-1104" />
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-1105">Développer</h3>
              <p className="text-sm text-gray-600" data-id="element-1106">
                Financer des ateliers de renforcement de capacités de qualité.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-100" data-id="element-1107">
            <CardContent className="p-6 text-center" data-id="element-1108">
              <Briefcase className="w-8 h-8 text-[#1E88E5] mx-auto mb-4" data-id="element-1109" />
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-1110">Insérer</h3>
              <p className="text-sm text-gray-600" data-id="element-1111">
                Soutenir les dispositifs de placement et mise en relation avec
                employeurs.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-100" data-id="element-1112">
            <CardContent className="p-6 text-center" data-id="element-1113">
              <TrendingUp className="w-8 h-8 text-[#1E88E5] mx-auto mb-4" data-id="element-1114" />
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-1115">Suivre</h3>
              <p className="text-sm text-gray-600" data-id="element-1116">
                Accompagner les bénéficiaires après leur placement pour garantir
                la durabilité.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Impact Statement */}
        <div className="bg-gradient-to-r from-[#1E88E5] to-[#1976D2] text-white rounded-xl p-8 mb-12 text-center" data-id="element-1117">
          <h2 className="text-2xl font-bold mb-4" data-id="element-1118">
            Votre don finance l'insertion professionnelle complète
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6" data-id="element-1119">
            <div data-id="element-1120">
              <p className="text-3xl font-bold mb-2" data-id="element-1121">50€</p>
              <p className="text-sm text-blue-100" data-id="element-1122">
                Finance 1 mois de suivi post-placement
              </p>
            </div>
            <div data-id="element-1123">
              <p className="text-3xl font-bold mb-2" data-id="element-1124">150€</p>
              <p className="text-sm text-blue-100" data-id="element-1125">
                Forme et place 1 bénéficiaire
              </p>
            </div>
            <div data-id="element-1126">
              <p className="text-3xl font-bold mb-2" data-id="element-1127">500€</p>
              <p className="text-sm text-blue-100" data-id="element-1128">
                Soutient le dispositif de placement pendant 1 mois
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1129">
          <div className="lg:col-span-2" data-id="element-1130">
            <Card data-id="element-1131">
              <CardContent className="p-8" data-id="element-1132">
                <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1133">
                  Informations du donateur
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1134">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1135">
                    <div data-id="element-1136">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1137">
                        Prénom *
                      </label>
                      <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-1138" />
                    </div>
                    <div data-id="element-1139">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1140">
                        Nom *
                      </label>
                      <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-1141" />
                    </div>
                  </div>

                  <div data-id="element-1142">
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1143">
                      Nom de l'entreprise (optionnel)
                    </label>
                    <input type="text" id="companyName" name="companyName" value={formData.companyName} onChange={handleInputChange} disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" placeholder="Si vous représentez une entreprise" data-id="element-1144" />
                  </div>

                  <div data-id="element-1145">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1146">
                      Email *
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" data-id="element-1147" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1148">
                    <div data-id="element-1149">
                      <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1150">
                        Montant du don *
                      </label>
                      <input type="number" id="amount" name="amount" value={formData.amount} onChange={handleInputChange} required disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border disabled:opacity-50" placeholder="0.00" min="1" step="0.01" data-id="element-1151" />
                    </div>
                    <div data-id="element-1152">
                      <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1153">
                        Devise *
                      </label>
                      <select id="currency" name="currency" value={formData.currency} onChange={handleInputChange} disabled={isProcessing} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-2 border bg-white disabled:opacity-50" data-id="element-1154">
                        <option value="EUR" data-id="element-1155">Euro (€)</option>
                        <option value="USD" data-id="element-1156">Dollar ($)</option>
                        <option value="XOF" data-id="element-1157">Franc CFA (XOF)</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-100" data-id="element-1158">
                    <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1159">
                      Moyen de paiement
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-1160">
                      <button type="button" onClick={() => {
                      setPaymentMethod('stripe');
                      setPaypalReady(false);
                    }} disabled={isProcessing} className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-all disabled:opacity-50 ${paymentMethod === 'stripe' ? 'border-[#1E88E5] bg-blue-50 ring-2 ring-[#1E88E5] ring-opacity-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-1161">
                        <CreditCard className="w-6 h-6 text-[#635BFF] mb-2" data-id="element-1162" />
                        <span className="font-bold text-[#635BFF]" data-id="element-1163">
                          Carte bancaire
                        </span>
                        <span className="text-xs text-gray-500 mt-1" data-id="element-1164">
                          via Stripe
                        </span>
                      </button>
                      <button type="button" onClick={() => setPaymentMethod('paypal')} disabled={isProcessing} className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-all disabled:opacity-50 ${paymentMethod === 'paypal' ? 'border-[#1E88E5] bg-blue-50 ring-2 ring-[#1E88E5] ring-opacity-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-1165">
                        <Wallet className="w-6 h-6 text-[#003087] mb-2" data-id="element-1166" />
                        <span className="font-bold text-[#003087]" data-id="element-1167">PayPal</span>
                        <span className="text-xs text-gray-500 mt-1" data-id="element-1168">
                          Compte PayPal
                        </span>
                      </button>
                    </div>

                    {/* PayPal Hosted Button Container */}
                    {paymentMethod === 'paypal' && <div className="mb-6" data-id="element-1169">
                        {paypalLoading && <div className="flex items-center justify-center gap-2 py-6 text-gray-500" data-id="element-1170">
                            <Loader2 className="w-5 h-5 animate-spin" data-id="element-1171" />
                            <span className="text-sm" data-id="element-1172">
                              Chargement de PayPal...
                            </span>
                          </div>}
                        <div id="paypal-button-container" ref={paypalContainerRef} className={`min-h-[50px] ${paypalLoading ? 'hidden' : ''}`} data-id="element-1173" />
                        {!paypalLoading && !paypalReady && <p className="text-xs text-gray-500 text-center mt-2" data-id="element-1174">
                            Cliquez sur "Procéder au paiement" pour être
                            redirigé vers PayPal.
                          </p>}
                      </div>}

                    {/* Show submit button for Stripe, or as fallback for PayPal */}
                    {(paymentMethod === 'stripe' || paymentMethod === 'paypal' && !paypalReady || !paymentMethod) && <Button type="submit" className="w-full" size="lg" disabled={isProcessing} data-id="element-1175">
                        {isProcessing ? <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-1176" />
                            Redirection en cours...
                          </> : !paymentMethod ? 'Sélectionnez un moyen de paiement' : paymentMethod === 'stripe' ? '💳 Payer par carte bancaire' : 'Procéder au paiement PayPal'}
                      </Button>}
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-6" data-id="element-1177">
            <Card className="bg-gray-50 border-gray-200" data-id="element-1178">
              <CardContent className="p-6" data-id="element-1179">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1180">
                  <Heart className="w-5 h-5 text-[#1E88E5]" data-id="element-1181" />
                  Impact Direct
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-1182">
                  100% de votre don finance directement :
                </p>
                <ul className="space-y-3 text-sm text-gray-700" data-id="element-1183">
                  <li className="flex items-start gap-2" data-id="element-1184">
                    <span className="text-[#1E88E5] font-bold" data-id="element-1185">•</span>
                    <span data-id="element-1186">Les ateliers de renforcement de capacités</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1187">
                    <span className="text-[#1E88E5] font-bold" data-id="element-1188">•</span>
                    <span data-id="element-1189">Les dispositifs d'insertion</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1190">
                    <span className="text-[#1E88E5] font-bold" data-id="element-1191">•</span>
                    <span data-id="element-1192">L'accompagnement post-insertion</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1193">
                    <span className="text-[#1E88E5] font-bold" data-id="element-1194">•</span>
                    <span data-id="element-1195">Le suivi et la médiation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gray-50 border-gray-200" data-id="element-1196">
              <CardContent className="p-6" data-id="element-1197">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1198">
                  <CreditCard className="w-5 h-5 text-[#1E88E5]" data-id="element-1199" />
                  Virement Bancaire
                </h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center" data-id="element-1200">
                  <p className="text-amber-800 font-medium text-sm mb-1" data-id="element-1201">
                    🏗️ Bientôt disponible
                  </p>
                  <p className="text-amber-700 text-xs" data-id="element-1202">
                    Le paiement par virement bancaire sera disponible
                    prochainement. En attendant, utilisez la carte bancaire ou
                    PayPal.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}